import React from "react";
import DashboardHeader from "./DashboardHeader";
import OverviewSection from "./OverviewSection";
import HistorySection from "./HistorySection";
import BillingSection from "./BillingSection";
import SettingsSection from "./SettingsSection";
import { ReferralsTab, AffiliateTab } from "./affiliate-referrals";
import { DashboardContentProps } from "./dashboard.types";
import styles from "./Dashboard.module.css";
import { useAuth } from "../../hooks/useAuth";

/**
 * Dashboard Content Component
 *
 * Main content area that displays different sections based on the active
 * navigation selection. Contains header, welcome section, and dynamic content.
 */
interface ExtendedDashboardContentProps extends DashboardContentProps {
  isMobileSidebarVisible?: boolean;
  onMobileSidebarToggle?: () => void;
}

const DashboardContent = ({
  activeSection,
  isMobileSidebarVisible = false,
  onMobileSidebarToggle,
}: ExtendedDashboardContentProps) => {
  const { user } = useAuth();
  /**
   * Render content based on active section
   * @returns JSX element for the selected section
   */
  const renderContent = () => {
    switch (activeSection) {
      case "overview":
        return <OverviewSection />;

      case "referrals":
        return (
          // ReferralsTab now manages its own layout and headers
          <ReferralsTab />
        );

      case "affiliate":
        return (
          // AffiliateTab now manages its own layout and headers
          <AffiliateTab />
        );

      case "history":
        return <HistorySection />;

      case "plans":
        return <BillingSection />;

      case "settings":
        return <SettingsSection />;

      default:
        return (
          <section className={styles.overviewSection}>
            {/* Fallback */}
            <p>Welcome to your dashboard!</p>
          </section>
        );
    }
  };

  return (
    <div className={styles.dashboardContent}>
      {/* Header section */}
      <DashboardHeader
        isMobileSidebarVisible={isMobileSidebarVisible}
        onMobileSidebarToggle={onMobileSidebarToggle}
      />

      {/* Dynamic content based on active section */}
      <div className={styles.contentSections}>
        {/* Welcome section */}
        <section className={styles.welcomeSection}>
          <h2 className={styles.welcomeTitle}>
            Welcome Back{" "}
            {user?.user_metadata?.full_name ||
              user?.user_metadata?.name ||
              user?.email ||
              "User"}
          </h2>
        </section>

        {/* Content sections */}
        {renderContent()}
      </div>
    </div>
  );
};

export default DashboardContent;
